import { z } from "zod";

/*
 * The lead's earlier conversation in another campaign of the same workspace, read for a reply to a
 * Bison follow-up campaign (`reply_followup`), or any campaign in an explicitly listed workspace
 * (Dawid, 2026-10-03: "Yes, for every campaign"). Original selection: Dawid, terminal picker about 19:46 CEST on
 * 2026-09-29, "Draft with it (Recommended)": "Read the lead's earlier Bison conversation, labelled
 * as an earlier campaign; drafts may refer to it; rep sees it in the thread."
 *
 * A follow-up campaign writes to leads who said yes to an earlier campaign and went quiet, in their
 * existing thread. Bison ties each reply to one campaign, so the thread import (`importReply`) sees
 * only the follow-up's own messages; what the lead said before reached drafting only as history
 * their reply happened to quote, cut and labelled as unverifiable
 * (`docs/2026-09-29-follow-up-earlier-conversation.md` has the measurement).
 *
 * These are verified Bison records of the same lead in the same workspace, so they are read, not
 * guessed at. The earlier campaign may have offered something else, though, so the stages get them
 * apart from the thread under one label and the `earlier_campaign` evidence authority, which the
 * host treats as it treats history quoted from outside the thread: background, never the lead's
 * statement in this conversation, never support for a claim, and never a source of this campaign's
 * offer or any other context value (`backgroundOnly` in `rules/types.ts`).
 *
 * Only the lead's own campaigns count: an earlier campaign whose messages are read is one the lead
 * replied to, and only before the reply being drafted. Nothing about a lead who never replied
 * (AGENTS.md, Dawid 17:06 CEST 2026-09-29). Pure, so it runs in the workflow sandbox.
 */

/** Bison's campaign type for an in-thread follow-up to leads of earlier campaigns. */
export const FOLLOW_UP_CAMPAIGN_TYPE = "reply_followup";

export const EARLIER_CONVERSATION_ALL_PATCH = "earlier-conversation-all-v1";
export const EARLIER_CONVERSATION_ALL_OFF_PATCH = "earlier-conversation-all-off-v1";

/** Import-time background is retained even when the worker later records the off decision. */
export const EarlierConversationAllRecordSchema = z.strictObject({
  version: z.literal(1),
  decision: z.enum(["on", "off"]),
});
export type EarlierConversationAllRecord = z.infer<typeof EarlierConversationAllRecordSchema>;

export const EARLIER_CONVERSATION_LIMITS = {
  /** Newest messages kept; older ones are counted in `omitted_messages`. */
  messages: 8,
  /** Characters of one message's own text; longer ones are cut and marked truncated. */
  perMessage: 2_000,
  /** Characters across all kept messages. */
  total: 8_000,
} as const;

export const EARLIER_CAMPAIGN_LABEL =
  "Earlier campaign, before this one. Background only: its offer, prices, links and promises are not this campaign's, and nothing in it is confirmed for this reply.";

const ProviderId = z.number().int().positive();
const Instant = z
  .string()
  .refine(
    (s) => /(?:Z|[+-]\d\d:?\d\d)$/.test(s) && Number.isFinite(Date.parse(s)),
    "Explicit timezone timestamp required",
  );

const EarlierMessageSchema = z.strictObject({
  /** Never a thread message ID (`bison:...`), so no rule can read it as one. */
  id: z.string().regex(/^earlier:(?:reply|sent_email):[1-9]\d*$/),
  provider_id: ProviderId,
  campaign_id: ProviderId,
  from: z.enum(["lead_side", "seller"]),
  date_received: Instant,
  subject: z.string().nullable(),
  text: z.string().min(1).max(EARLIER_CONVERSATION_LIMITS.perMessage),
  truncated: z.boolean(),
  original_characters: z.number().int().positive(),
});
type EarlierMessage = z.infer<typeof EarlierMessageSchema>;

export const EarlierConversationSchema = z
  .strictObject({
    label: z.literal(EARLIER_CAMPAIGN_LABEL),
    /** The earlier campaigns the lead replied to, with Bison's name where it was read. */
    campaigns: z.array(z.strictObject({ id: ProviderId, name: z.string().nullable() })).min(1),
    messages: z.array(EarlierMessageSchema).min(1).max(EARLIER_CONVERSATION_LIMITS.messages),
    omitted_messages: z.number().int().nonnegative(),
  })
  .superRefine((value, ctx) => {
    const ids = value.messages.map((message) => message.id);
    if (new Set(ids).size !== ids.length)
      ctx.addIssue({ code: "custom", path: ["messages"], message: "Duplicate earlier message" });
    const campaigns = new Set(value.campaigns.map((campaign) => campaign.id));
    if (campaigns.size !== value.campaigns.length)
      ctx.addIssue({ code: "custom", path: ["campaigns"], message: "Duplicate earlier campaign" });
    if (value.messages.some((message) => !campaigns.has(message.campaign_id)))
      ctx.addIssue({
        code: "custom",
        path: ["messages"],
        message: "An earlier message names a campaign the conversation does not list",
      });
    const total = value.messages.reduce((sum, message) => sum + message.text.length, 0);
    if (total > EARLIER_CONVERSATION_LIMITS.total)
      ctx.addIssue({
        code: "custom",
        path: ["messages"],
        message: "Earlier conversation too long",
      });
    for (let index = 1; index < value.messages.length; index++) {
      const before = value.messages[index - 1];
      const after = value.messages[index];
      if (before && after && Date.parse(before.date_received) > Date.parse(after.date_received))
        ctx.addIssue({
          code: "custom",
          path: ["messages", index],
          message: "Earlier messages are kept in the order they were written",
        });
    }
  });
export type EarlierConversation = z.infer<typeof EarlierConversationSchema>;

/** One earlier message as the provider read it, before the bounds. */
export interface EarlierCandidate {
  readonly id: EarlierMessage["id"];
  readonly provider_id: number;
  readonly campaign_id: number;
  readonly from: EarlierMessage["from"];
  readonly date_received: string;
  readonly subject: string | null;
  readonly text: string;
}

/**
 * The newest messages that fit the bounds, oldest first. A message that would take the total past
 * its bound is cut to what is left, and only a message with nothing left is omitted, so the newest
 * exchange, the one the follow-up refers to, always survives.
 */
export function boundEarlierConversation(
  candidates: readonly EarlierCandidate[],
  campaigns: readonly { id: number; name: string | null }[],
): EarlierConversation | null {
  const usable = candidates
    .map((candidate) => ({ ...candidate, text: candidate.text.trim() }))
    .filter((candidate) => candidate.text.length > 0)
    .sort(
      (a, b) =>
        Date.parse(b.date_received) - Date.parse(a.date_received) || b.id.localeCompare(a.id),
    );
  const kept: EarlierMessage[] = [];
  let remaining: number = EARLIER_CONVERSATION_LIMITS.total;
  for (const candidate of usable) {
    if (kept.length === EARLIER_CONVERSATION_LIMITS.messages || remaining <= 0) break;
    const room = Math.min(EARLIER_CONVERSATION_LIMITS.perMessage, remaining);
    const text = candidate.text.slice(0, room).trim();
    if (!text) break;
    remaining -= text.length;
    kept.push({
      id: candidate.id,
      provider_id: candidate.provider_id,
      campaign_id: candidate.campaign_id,
      from: candidate.from,
      date_received: candidate.date_received,
      subject: candidate.subject,
      text,
      truncated: candidate.text.length > text.length,
      original_characters: candidate.text.length,
    });
  }
  if (!kept.length) return null;
  const used = new Set(kept.map((message) => message.campaign_id));
  return EarlierConversationSchema.parse({
    label: EARLIER_CAMPAIGN_LABEL,
    campaigns: campaigns.filter((campaign) => used.has(campaign.id)),
    messages: kept.reverse(),
    omitted_messages: usable.length - kept.length,
  });
}

/** What a model stage sees: no campaign identity or name, no subject, the label on top. */
export const EarlierConversationViewSchema = z.strictObject({
  label: z.literal(EARLIER_CAMPAIGN_LABEL),
  messages: z
    .array(
      z.strictObject({
        evidence_id: EarlierMessageSchema.shape.id,
        from: EarlierMessageSchema.shape.from,
        date_received: Instant,
        text: EarlierMessageSchema.shape.text,
        truncated: z.boolean(),
      }),
    )
    .min(1)
    .max(EARLIER_CONVERSATION_LIMITS.messages),
  omitted_messages: z.number().int().nonnegative(),
});
export type EarlierConversationView = z.infer<typeof EarlierConversationViewSchema>;

export function earlierConversationView(
  conversation: EarlierConversation,
): EarlierConversationView {
  return EarlierConversationViewSchema.parse({
    label: conversation.label,
    messages: conversation.messages.map((message) => ({
      evidence_id: message.id,
      from: message.from,
      date_received: message.date_received,
      text: message.text,
      truncated: message.truncated,
    })),
    omitted_messages: conversation.omitted_messages,
  });
}

/** The evidence text of one earlier message: the label, who wrote it and when, then its text. */
export function earlierEvidenceText(
  message: Pick<EarlierConversationView["messages"][number], "from" | "date_received" | "text">,
): string {
  const who = message.from === "seller" ? "Seller" : "Lead side";
  return `${EARLIER_CAMPAIGN_LABEL} ${who}, ${message.date_received}:\n${message.text}`;
}
